
import ServiceTemplate from '../ServiceTemplate';
import SEOHead from '../../../components/SEOHead';

export default function ResidentialElectricalPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Residential Electrical Services',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US'
      }
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California'
    },
    description: 'Professional residential electrical services in Fresno including wiring, panel upgrades, outlet installation, lighting, ceiling fans, surge protection, and home electrical repairs by licensed C-10 electricians',
    url: `${siteUrl}/services/residential-electrical`
  };

  const features = [
    { 
      icon: 'ri-lightbulb-line', 
      title: 'Lighting Installation', 
      description: 'Indoor and outdoor lighting solutions — recessed lights, chandeliers, landscape lighting, and LED upgrades for every room and space in your Fresno home' 
    },
    { 
      icon: 'ri-plug-line', 
      title: 'Outlet & Switch Installation', 
      description: 'New outlets, USB charging ports, GFCI protection for kitchens and bathrooms, dimmer switches, and smart switch upgrades throughout your home' 
    },
    { 
      icon: 'ri-dashboard-line', 
      title: 'Panel Upgrades', 
      description: '100A to 200A+ electrical panel upgrades to handle modern electrical demands — AC units, pool equipment, EV chargers, and high-power appliances' 
    },
    { 
      icon: 'ri-alarm-warning-line', 
      title: 'Circuit Breaker Replacement', 
      description: 'Replace outdated, faulty, or frequently tripping breakers with modern AFCI and GFCI circuit breakers for enhanced safety and code compliance' 
    },
    { 
      icon: 'ri-home-wifi-line', 
      title: 'Whole-Home Rewiring', 
      description: 'Complete electrical rewiring for older Fresno homes with outdated knob-and-tube or aluminum wiring — ensuring safety and meeting California Title 24 standards' 
    },
    { 
      icon: 'ri-shield-check-line', 
      title: 'Safety Inspections', 
      description: 'Comprehensive electrical safety inspections, code compliance checks, and detailed reports for home buyers, sellers, and homeowners in Fresno County' 
    },
    { 
      icon: 'ri-windy-line', 
      title: 'Ceiling Fan Installation', 
      description: 'Professional ceiling fan installation and replacement — keep your Fresno home cool during hot Central Valley summers with energy-efficient fan solutions' 
    },
    { 
      icon: 'ri-flashlight-line', 
      title: 'Surge Protection', 
      description: 'Whole-home surge protection systems to safeguard your electronics, appliances, and HVAC equipment from power surges and lightning strikes' 
    },
  ];

  const whatsIncluded = [
    'Free on-site consultation and detailed quote for all Fresno residential projects',
    'Licensed C-10 electricians with 15+ years of experience in Fresno and Central Valley',
    'Full compliance with California Title 24 electrical codes and local Fresno building requirements',
    'High-quality materials and equipment from trusted brands — no cheap substitutes',
    'Clean, professional work with minimal disruption to your home and daily routine',
    'Thorough testing and inspection of all electrical work before project completion',
    'Detailed documentation and permits pulled for all major electrical installations',
    'Warranty on all labor and materials — your satisfaction and safety are guaranteed',
  ];

  return (
    <>
      <SEOHead
        title="Residential Electrician Fresno CA | Home Electrical Services"
        description="Expert residential electrical services in Fresno, CA. Panel upgrades, rewiring, outlet installation, lighting, ceiling fans, and home electrical repairs. Licensed C-10 electricians. Free quotes."
        canonical="/services/residential-electrical"
        keywords="residential electrician Fresno, home electrical services Fresno, panel upgrade Fresno, house rewiring Fresno CA"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Residential Electrician — Fresno, CA"
        heroSubtitle="Complete electrical solutions for your Fresno home — licensed, insured & 5-star rated"
        heroImage="https://public.readdy.ai/ai/img_res/e5c0af899537f9b97c4b2317602b65df.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Professional%20residential%20electrical%20panel%20installation%20with%20organized%20circuit%20breakers%20colour%20coded%20wiring%20copper%20conductors%20and%20neat%20cable%20management%20mounted%20on%20white%20wall%20in%20modern%20California%20home%20interior%20dramatic%20side%20lighting%20no%20people&width=700&height=600&seq=res-fresno-panel-02&orientation=portrait"
        tagline="Trusted by 500+ Fresno Homeowners"
        fresnoContent="Serving Fresno homeowners with licensed residential electrical services since day one. Whether you're upgrading an older home in the Tower District, adding outlets in a Woodward Park property, installing ceiling fans in Fig Garden, or rewiring a historic home downtown, our Fresno-based C-10 licensed electricians ensure every job meets California Title 24 standards and local code requirements. We understand Fresno's unique electrical needs — from handling summer heat loads and AC demands to ensuring your home is ready for modern appliances, smart home technology, and EV charging. From simple outlet installations to complete panel upgrades and whole-home rewiring, we deliver safe, reliable, and code-compliant electrical work you can trust."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          { 
            question: 'Are your residential electricians licensed in Fresno, CA?', 
            answer: 'Yes, all our electricians are fully licensed by the California Contractors State License Board (CSLB) with active C-10 electrical contractor licenses and are fully insured to work in Fresno and throughout Fresno County. We stay current with all California Title 24 electrical codes, NEC standards, and local Fresno building codes to ensure every residential project is safe, compliant, and up to code.' 
          },
          { 
            question: 'How much does residential electrical work cost in Fresno?', 
            answer: 'Costs vary by project scope and complexity. Simple outlet or switch installations typically start around $150–$250, ceiling fan installations range from $200–$400, lighting upgrades run $300–$800 depending on fixtures, and electrical panel upgrades typically range from $1,500–$3,500 depending on amperage and complexity. Whole-home rewiring projects vary widely based on home size and condition. We provide free, detailed quotes for all Fresno residential projects with transparent pricing and no hidden fees.' 
          },
          { 
            question: 'Do you service all areas of Fresno?', 
            answer: 'Absolutely. We serve all Fresno neighborhoods including Tower District, Woodward Park, Fig Garden, Old Fig Garden, Sunnyside, McLane, Bullard, Herndon, and surrounding areas. We also cover Clovis, Madera, Sanger, Selma, and other nearby communities throughout the Central Valley. If you\'re in Fresno County or the surrounding area, we can help with your residential electrical needs.' 
          }, // <-- added missing comma here
          { 
            question: 'Can you upgrade my electrical panel to support more appliances?', 
            answer: 'Yes. Many older Fresno homes have 100-amp electrical panels that can\'t safely handle modern electrical loads — especially with central AC units, pool equipment, hot tubs, electric vehicle chargers, and high-power kitchen appliances. We upgrade residential panels to 200 amps or higher, ensuring your home can safely power everything you need while meeting current California electrical codes and Title 24 requirements. Panel upgrades also improve safety and can increase your home\'s resale value.' 
          },
          { 
            question: 'Do you offer emergency residential electrical services in Fresno?', 
            answer: 'Yes, we provide emergency electrical services for Fresno homeowners. If you\'re experiencing power outages, sparking outlets, burning smells, tripped breakers that won\'t reset, flickering lights, or any other urgent electrical issues, call us immediately. We prioritize safety and respond quickly to urgent electrical problems throughout Fresno and Fresno County. Electrical emergencies can be dangerous — don\'t wait, contact us right away.' 
          },
          { 
            question: 'How long does a typical residential electrical project take in Fresno?', 
            answer: 'Timeline depends on the project scope. Simple outlet or switch installations take 1–2 hours, ceiling fan installations take 2–3 hours, lighting upgrades take 2–4 hours depending on the number of fixtures, and electrical panel upgrades typically take 4–8 hours including inspection and testing. Whole-home rewiring projects can take several days to a few weeks depending on home size and complexity. We\'ll provide a clear, realistic timeline during your free Fresno consultation and work efficiently to minimize disruption to your home and daily routine.' 
          },
        ]}
        serviceSlug="residential-electrical"
      />
    </>
  );
}
